import React from 'react';
import { Button } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { HeadingMedium } from 'baseui/typography';
import { useIngestDodProjectFiles } from '../../hooks/use-ingest-dod-project-files';
const isImage = (path: string) => {
  return /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(path);
};
const isVideo = (path: string) => {
  return /\.(mp4|webm|og[gv]|mov|avi|mkv)$/i.test(path);
};
const downloadCsv = (csvContent: string[], filename: string) => {
  const blob = new Blob([csvContent.join('\n')], {
    type: 'text/csv'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
const handleCsvGeneration = (successfulUrls: string[], filename: string) => {
  const imageUrls = successfulUrls.filter(isImage);
  const videoUrls = successfulUrls.filter(isVideo);
  if (imageUrls.length > 0) {
    const csvLines = ['image_url,task_id'];
    imageUrls.forEach((url, index) => {
      csvLines.push(`${url},${index + 1}`);
    });
    downloadCsv(csvLines, `${filename}_images.csv`);
  }
  if (videoUrls.length > 0) {
    const csvLines = ['video_url,task_id'];
    videoUrls.forEach((url, index) => {
      csvLines.push(`${url},${index + 1}`);
    });
    downloadCsv(csvLines, `${filename}_videos.csv`);
  }
};
const DoDMediaIngestion = () => {
  const [claimId, setClaimId] = React.useState<string>('');
  const [recipientCode, setRecipientCode] = React.useState<string>('');
  const [claimPasscode, setClaimPasscode] = React.useState<string>('');
  const [projectId, setProjectId] = React.useState<number>(1);
  const {
    isPending,
    ingestDoDProjectFiles
  } = useIngestDodProjectFiles(claimId, recipientCode, claimPasscode, projectId);
  const handleSubmit = () => {
    ingestDoDProjectFiles({
      claimId,
      recipientCode,
      claimPasscode,
      projectId
    }, {
      onSuccess: response => {
        if (response.data.failedFiles && response.data.failedFiles.length > 0) {
          response.data.failedFiles.forEach(file => {
            console.error(`Failed to copy file ${file.objectName}: ${file.reason}`);
            toaster.negative(`Failed to copy file ${file.objectName}: ${file.reason}`, {
              key: 'ingestDoDProjectFilesError' + file.objectName
            });
          });
          return;
        }
        handleCsvGeneration(response.data.successfulUrls, `dod_safe_project_${projectId}_claim_${claimId}`);
        toaster.positive(`Successfully ingested ${response.data.successfulUrls.length} files`, {
          key: 'ingestDoDProjectFilesSuccess'
        });
      },
      onError: error => {
        console.error('Error ingesting DoD Safe files:', error);
        toaster.negative('Error ingesting DoD Safe files: ' + error, {
          key: 'ingestDoDProjectFilesError'
        });
      }
    });
  };
  if (isPending) {
    return <Spinner />;
  }
  const isFormInvalid = !claimId || !recipientCode || !claimPasscode || !projectId;
  return <div style={{
    width: '100%'
  }}>
      <HeadingMedium>Ingest files from DoD Safe to GCS</HeadingMedium>
      <FormControl label="Project Id (under which media will be copied)" error={!projectId ? 'Project id is required' : ''}>
        <Input value={projectId} onChange={e => setProjectId(+e.currentTarget.value)} type="number" min={1} placeholder="Enter project id" />
      </FormControl>
      <FormControl label="DoD Claim Id" error={!claimId ? 'DoD claim id is required' : ''}>
        <Input value={claimId} onChange={e => setClaimId(e.currentTarget.value)} placeholder="Enter DoD Safe Claim Id" />
      </FormControl>
      <FormControl label="DoD Recipient Code" error={!recipientCode ? 'DoD recipient code is required' : ''}>
        <Input value={recipientCode} onChange={e => setRecipientCode(e.currentTarget.value)} placeholder="Enter DoD Safe Recipient Code" />
      </FormControl>
      <FormControl label="DoD Claim Passcode" error={!claimPasscode ? 'DoD claim passcode is required' : ''}>
        <Input value={claimPasscode} onChange={e => setClaimPasscode(e.currentTarget.value)} placeholder="Enter DoD Safe Claim Passcode" />
      </FormControl>
      <Button disabled={isFormInvalid} onClick={() => {
      handleSubmit();
    }}>
        Ingest Files and generate CSV
      </Button>
    </div>;
};
export default DoDMediaIngestion;